import * as React from "react";
import { useRef } from "react";
import { Button, CircularProgress, styled, TextField } from "@mui/material";
import { SupportedLanguages } from "@kahunmedical/common-configuration/dist/configuration/supportedLanguages";
import { KAHUN_PLATFORM } from "../cognito/kahunUserAuth";
import { useLogin } from "../hooks/useLogin";
import { GoogleButton } from "./googleButton";

//region [[ Styles ]]
const OrSeparator = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "8px 5px",
  gap: 10,
  fontSize: 12,
  fontWeight: 700,
  "& hr": {
    borderColor: theme.palette.divider,
    borderWidth: "1px 0 0 0",
    borderStyle: "solid",
    flex: 1,
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface LoginFormProps {
  onSignIn: (email?: string) => void;
  onError?: (error: any) => void;
  disabled?: boolean;
  keepLoading?: boolean;
  onLoading?: () => void;
  onLoadingDone?: () => void;
  platform: KAHUN_PLATFORM;
  clinic?: string;
  locale?: string;
  enableGoogleLogin: boolean;
  socialLoginRedirect?: string;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LoginFormMui = ({
  onLoading,
  onLoadingDone,
  keepLoading,
  onSignIn,
  onError,
  clinic = null,
  locale = SupportedLanguages.English_US,
  platform = KAHUN_PLATFORM.UNKNOWN,
  enableGoogleLogin = true,
  socialLoginRedirect,
}: LoginFormProps) => {
  const passwordRef = useRef<HTMLInputElement>();

  const {
    setEmail,
    setPassword,
    setVerificationCode,
    verificationCodeFn,
    loading,
    handleVerify,
    handleSignIn,
  } = useLogin({
    onLoading,
    onLoadingDone,
    keepLoading,
    onSignIn,
    onError,
    clinic,
    locale,
    platform,
  });

  const handleEmailChange = (e) => setEmail(e.target.value);

  const handlePasswordChange = (e) => setPassword(e.target.value);

  const handleVerificationCode = (e) => setVerificationCode(e.target.value);

  const handleEnterInEmail = (e) => {
    if ((e.key === "Enter" || e.Key === "F12") && passwordRef.current) {
      console.log(e);
      passwordRef.current.focus();
    }
  };

  const handleEnterInPassword = (e) => {
    if (e.key === "Enter" || e.Key === "F12") {
      handleSignIn(e);
    }
  };

  return (
    <>
      <form>
        <TextField
          className={"email-input"}
          name="username"
          margin="normal"
          variant="outlined"
          label="Email Address"
          type="email"
          autoComplete="username"
          fullWidth
          required
          onChange={handleEmailChange}
          onKeyDown={handleEnterInEmail}
        />
        <TextField
          className={"password-input"}
          ref={passwordRef}
          name="password"
          margin="normal"
          variant="outlined"
          label="Password"
          type="password"
          autoComplete="password"
          fullWidth
          required
          onChange={handlePasswordChange}
          onKeyDown={handleEnterInPassword}
        />
        <Button
          variant="contained"
          fullWidth
          color="primary"
          type="button"
          onClick={handleSignIn}
          disabled={loading}
          className="submit-button"
        >
          {loading ? <CircularProgress size={20} /> : "Log In"}
        </Button>
      </form>
      {enableGoogleLogin && (
        <>
          <OrSeparator>
            <hr />
            Or
            <hr />
          </OrSeparator>
          <GoogleButton redirectTo={socialLoginRedirect} />
        </>
      )}
      {verificationCodeFn && (
        <div style={{ marginTop: 10 }}>
          <form>
            <div>Enter the verification code you received in your email.</div>
            <div>
              (If you don't see the email, check the spam folder for mail from
              info@kahun.com)
            </div>
            <TextField
              fullWidth
              required
              margin="normal"
              variant="outlined"
              label="Verification Code"
              onChange={handleVerificationCode}
            />
            <Button
              variant="contained"
              fullWidth
              color={"primary"}
              onClick={handleVerify}
            >
              Verify
            </Button>
            <Button color={"primary"} onClick={verificationCodeFn}>
              Resend Code
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
